import { useState, useEffect, useRef } from 'react';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import ScrollReveal from '@/components/ScrollReveal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Calendar,
  Target,
  FlaskConical,
  CheckCircle,
  ArrowRight,
  Users,
  Lightbulb,
  Landmark,
  Briefcase,
  Code,
} from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const contactOptions = [
  {
    icon: Calendar,
    title: 'Discovery Call',
    description:
      "30-45 minute call with a founding team member to understand your challenges, explore opportunities, and determine if we're a good fit.",
    outcomes: [
      'Assessment of your AI readiness',
      'Identification of high-value use-cases',
      'Rough ROI estimation',
      'Recommended next steps',
    ],
    cta: 'Book Discovery Call',
    inquiryType: 'Discovery Call',
  },
  {
    icon: FlaskConical,
    title: 'PoC Scoping Workshop',
    description:
      'Half-day workshop with stakeholders to define a proof-of-concept scope, success criteria, and implementation plan.',
    outcomes: [
      'Detailed workflow mapping',
      'Technical architecture proposal',
      'Success metrics and evaluation framework',
      'Statement of work and timeline',
    ],
    cta: 'Schedule Workshop',
    inquiryType: 'PoC Scoping Workshop',
  },
];

const whoShouldContact = [
  {
    icon: Briefcase,
    title: 'Enterprise Technology Leaders',
    description:
      'CTOs, CIOs, and VPs of Engineering looking to deploy AI in production with proper governance and security controls.',
  },
  {
    icon: Lightbulb,
    title: 'Innovation & Strategy Teams',
    description:
      'Chief Innovation Officers and heads of digital transformation exploring AI opportunities across the organisation.',
  },
  {
    icon: Landmark,
    title: 'Regulated Industry Operators',
    description:
      'Leaders in FinTech, Pharma, Telecom, and Government requiring compliant, auditable AI systems.',
  },
  {
    icon: Users,
    title: 'Executive Leadership',
    description:
      'CEOs and CFOs seeking decision support systems and operational efficiency through AI automation.',
  },
  {
    icon: Code,
    title: 'Product & Engineering Teams',
    description:
      'Product leaders and engineering managers building AI features into existing platforms.',
  },
  {
    icon: Target,
    title: 'Investment & Advisory',
    description:
      'Investors and board members evaluating AI opportunities or conducting technical due diligence.',
  },
];

const expectItems = [
  {
    number: '1',
    title: 'Honest assessment',
    description:
      "We'll tell you if AI is the right solution, or if there are simpler approaches that will deliver better value.",
  },
  {
    number: '2',
    title: 'Senior engagement',
    description:
      "You'll work directly with founding team members who have decades of enterprise experience, not junior staff.",
  },
  {
    number: '3',
    title: 'No sales pressure',
    description:
      "Our goal is to understand if we can create real value for you. If we're not the right fit, we'll tell you.",
  },
  {
    number: '4',
    title: 'Concrete next steps',
    description:
      'Every conversation ends with clear recommendations: specific use-cases, ROI estimates, and a proposed path forward.',
  },
];


export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    inquiryType: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [hcaptchaToken, setHcaptchaToken] = useState('');
  const hcaptchaRef = useRef<HCaptcha>(null);

  useEffect(() => {
    ScrollTrigger.refresh();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!hcaptchaToken) {
      setSubmitError('Please complete the captcha.');
      return;
    }
    setIsSubmitting(true);
    setSubmitError('');

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          access_key: "a102de75-f580-493d-b5cd-b945201bc3a5",
          "h-captcha-response": hcaptchaToken,
          name: formData.name,
          email: formData.email,
          company: formData.company,
          inquiry_type: formData.inquiryType,
          message: formData.message,
          subject: `New ${formData.inquiryType || 'Contact'} from ${formData.name}`,
        }),
      });

      const data = await response.json();
      console.log('Web3Forms response:', data);

      if (data.success) {
        setIsSubmitted(true);
        setFormData({ name: '', email: '', company: '', inquiryType: '', message: '' });
      } else {
        setSubmitError(data.message || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setSubmitError('Network error. Please check your connection and try again.');
    }

    setHcaptchaToken('');
    hcaptchaRef.current?.resetCaptcha();
    setIsSubmitting(false);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        {/* Page Header */}
        <section className="pt-44 pb-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />
          <div className="container-custom relative z-10">
            <ScrollReveal className="text-center max-w-3xl mx-auto">
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-slate-900 mb-6">
                Let's Map Your Highest-Value AI Workflows
              </h1>
              <p className="text-lg sm:text-xl text-slate-600">
                Book a discovery call to discuss how production-grade AI can
                create measurable value in your organisation
              </p>
            </ScrollReveal>
          </div>
        </section>

        {/* Contact Options */}
        <section className="py-10">
          <div className="container-custom">
            <ScrollReveal className="text-center mb-8">
              <p className="text-slate-600 max-w-2xl mx-auto">
                If you want production-grade AI that is secure, governed, and
                delivers measurable ROI, we should talk.
              </p>
            </ScrollReveal>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {contactOptions.map((option, index) => (
                <ScrollReveal key={index} delay={index * 0.1}>
                  <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-card h-full flex flex-col">
                    <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                      <option.icon className="w-7 h-7 text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">
                      {option.title}
                    </h3>
                    <p className="text-slate-600 text-sm mb-4 flex-grow">
                      {option.description}
                    </p>
                    <div className="mb-4">
                      <h4 className="text-slate-900 font-medium text-sm mb-2">
                        What you'll get:
                      </h4>
                      <ul className="space-y-1">
                        {option.outcomes.map((outcome, i) => (
                          <li
                            key={i}
                            className="flex items-start gap-2 text-slate-500 text-xs"
                          >
                            <CheckCircle className="w-3 h-3 text-primary mt-0.5 flex-shrink-0" />
                            {outcome}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <Button
                      variant="outline"
                      className="w-full border-primary text-primary hover:bg-primary hover:text-white"
                      onClick={() => {
                        setFormData({ ...formData, inquiryType: option.inquiryType });
                        document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
                      }}
                    >
                      {option.cta}
                    </Button>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="section-padding bg-slate-50" id="contact-form">
          <div className="container-custom">
            <div className="max-w-2xl mx-auto">
              <ScrollReveal className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                  Send Us a Message
                </h2>
                <p className="text-slate-600">
                  Have a question or want to discuss how AI can create value for
                  your organisation? Fill out the form below and we'll get back to
                  you within 24 hours.
                </p>
              </ScrollReveal>

              <ScrollReveal>
                {isSubmitted ? (
                  <div className="bg-white rounded-xl p-8 text-center border border-primary/20">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-primary" />
                    </div>
                    <h3 className="text-2xl font-semibold text-slate-900 mb-2">
                      Message Sent!
                    </h3>
                    <p className="text-slate-600">
                      Thank you for reaching out. We'll get back to you within 24
                      hours.
                    </p>
                  </div>
                ) : (
                  <form
                    onSubmit={handleSubmit}
                    className="bg-white rounded-xl p-8 border border-slate-200"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                      <div>
                        <Label htmlFor="name" className="text-slate-900 mb-2 block">
                          Full Name <span className="text-primary">*</span>
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          required
                          placeholder="Your full name"
                          value={formData.name}
                          onChange={handleInputChange}
                          className="bg-white border-slate-200 text-slate-900 placeholder:text-slate-400"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email" className="text-slate-900 mb-2 block">
                          Email Address <span className="text-primary">*</span>
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          required
                          placeholder="you@company.com"
                          value={formData.email}
                          onChange={handleInputChange}
                          className="bg-white border-slate-200 text-slate-900 placeholder:text-slate-400"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                      <div>
                        <Label htmlFor="company" className="text-slate-900 mb-2 block">
                          Company
                        </Label>
                        <Input
                          id="company"
                          name="company"
                          type="text"
                          placeholder="Your company name"
                          value={formData.company}
                          onChange={handleInputChange}
                          className="bg-white border-slate-200 text-slate-900 placeholder:text-slate-400"
                        />
                      </div>
                      <div>
                        <Label
                          htmlFor="inquiryType"
                          className="text-slate-900 mb-2 block"
                        >
                          Inquiry Type
                        </Label>
                        <input type="hidden" name="inquiryType" value={formData.inquiryType} />
                        <Select
                          value={formData.inquiryType}
                          onValueChange={(value) =>
                            setFormData({ ...formData, inquiryType: value })
                          }
                        >
                          <SelectTrigger className="bg-white border-slate-200 text-slate-900">
                            <SelectValue placeholder="Select an option" />
                          </SelectTrigger>
                          <SelectContent className="bg-white border-slate-200">
                            <SelectItem value="Discovery Call">
                              Discovery Call
                            </SelectItem>
                            <SelectItem value="PoC Scoping Workshop">
                              PoC Scoping Workshop
                            </SelectItem>
                            <SelectItem value="General Inquiry">
                              General Inquiry
                            </SelectItem>
                            <SelectItem value="Partnership">Partnership</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="mb-6">
                      <Label htmlFor="message" className="text-slate-900 mb-2 block">
                        Message <span className="text-primary">*</span>
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        rows={6}
                        placeholder="Tell us about your challenges, goals, or questions..."
                        value={formData.message}
                        onChange={handleInputChange}
                        className="bg-white border-slate-200 text-slate-900 placeholder:text-slate-400 resize-none"
                      />
                    </div>

                    <div className="mb-6">
                      <HCaptcha
                        sitekey="50b2fe65-b00b-4b9e-ad62-3ba471098be2"
                        reCaptchaCompat={false}
                        onVerify={setHcaptchaToken}
                        ref={hcaptchaRef}
                      />
                    </div>

                    {submitError && (
                      <p className="text-red-600 text-sm mb-4">{submitError}</p>
                    )}

                    <button
                      type="submit"
                      disabled={isSubmitting || !hcaptchaToken}
                      className="w-full bg-primary text-white px-8 py-3.5 rounded-lg font-semibold hover:bg-primary-dark transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isSubmitting ? (
                        'Sending...'
                      ) : (
                        <>
                          Send Message
                          <ArrowRight className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  </form>
                )}
              </ScrollReveal>
            </div>
          </div>
        </section>

        {/* Who Should Contact Us */}
        <section className="section-padding">
          <div className="container-custom">
            <ScrollReveal className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                Who Should Contact Us
              </h2>
            </ScrollReveal>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {whoShouldContact.map((item, index) => (
                <ScrollReveal key={index} delay={index * 0.1}>
                  <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                      <item.icon className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="text-slate-900 font-semibold mb-2">
                      {item.title}
                    </h3>
                    <p className="text-slate-600 text-sm">
                      {item.description}
                    </p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* What to Expect */}
        <section className="section-padding bg-slate-50">
          <div className="container-custom">
            <ScrollReveal className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                What to Expect from Our Discovery Process
              </h2>
            </ScrollReveal>

            <div className="max-w-3xl mx-auto space-y-6">
              {expectItems.map((item, index) => (
                <ScrollReveal key={index} delay={index * 0.1}>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold">{item.number}</span>
                    </div>
                    <div>
                      <h3 className="text-slate-900 font-semibold mb-1">
                        {item.title}
                      </h3>
                      <p className="text-slate-600">{item.description}</p>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>



        {/* Final CTA */}
        <section className="section-padding">
          <div className="container-custom">
            <ScrollReveal className="text-center max-w-2xl mx-auto">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                Ready to deploy AI that works in the real world?
              </h2>
              <p className="text-slate-600 mb-8">
                Book a discovery call to map high-value workflows and define a
                production path.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <button
                  onClick={() => {
                    const el = document.getElementById('contact-form');
                    if (el) {
                      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                  className="btn-primary inline-flex items-center gap-2 group"
                >
                  Book a Discovery Call
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </button>
                <a
                  href="mailto:contact@codriverlabs.ai"
                  className="btn-secondary"
                >
                  Send a Message
                </a>
              </div>
            </ScrollReveal>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
